import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  Shield, 
  BarChart3, 
  ArrowRight,
  Upload,
  Target,
  History,
  ChevronRight,
  CheckCircle,
  Flame,
  Scale,
  User,
  Clock,
  Percent,
  TrendingDown,
  Sparkles,
  Activity,
  Trophy,
  LineChart,
  Eye
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Risk Profile Questionnaire Questions
const QUESTIONS = [
  {
    id: 1,
    question: "Quel est votre niveau d'expérience en trading ?",
    questionEn: "What is your trading experience level?",
    icon: User,
    options: [
      { label: "Débutant (< 6 mois)", labelEn: "Beginner (< 6 months)", score: 1 },
      { label: "Intermédiaire (6 mois - 2 ans)", labelEn: "Intermediate (6 months - 2 years)", score: 2 },
      { label: "Confirmé (2 - 5 ans)", labelEn: "Experienced (2 - 5 years)", score: 3 },
      { label: "Expert (> 5 ans)", labelEn: "Expert (> 5 years)", score: 4 },
    ]
  },
  {
    id: 2,
    question: "Face à un krach de -40% sur le marché, quelle est votre réaction ?",
    questionEn: "Facing a -40% market crash, what's your reaction?",
    icon: TrendingDown,
    options: [
      { label: "Je suis très anxieux, je préfère sécuriser", labelEn: "Very anxious, I prefer to secure", score: 1 },
      { label: "Je suis inquiet mais je ne touche à rien", labelEn: "Worried but I don't touch anything", score: 2 },
      { label: "C'est normal, je reste sur ma stratégie", labelEn: "It's normal, I stick to my strategy", score: 3 },
      { label: "Excellent ! C'est le moment d'acheter", labelEn: "Excellent! Time to buy more", score: 4 },
    ]
  },
  {
    id: 3,
    question: "Quel pourcentage de votre capital êtes-vous prêt à risquer par trade ?",
    questionEn: "What percentage of your capital are you willing to risk per trade?",
    icon: Percent,
    options: [
      { label: "0.5% - 1% (très conservateur)", labelEn: "0.5% - 1% (very conservative)", score: 1 },
      { label: "1% - 2% (modéré)", labelEn: "1% - 2% (moderate)", score: 2 },
      { label: "2% - 5% (agressif)", labelEn: "2% - 5% (aggressive)", score: 3 },
      { label: "> 5% (très agressif)", labelEn: "> 5% (very aggressive)", score: 4 },
    ]
  },
  {
    id: 4,
    question: "Quel est votre style de trading préféré ?",
    questionEn: "What is your preferred trading style?",
    icon: Clock,
    options: [
      { label: "Scalping (minutes)", labelEn: "Scalping (minutes)", score: 1 },
      { label: "Day trading (heures)", labelEn: "Day trading (hours)", score: 2 },
      { label: "Swing trading (jours/semaines)", labelEn: "Swing trading (days/weeks)", score: 3 },
      { label: "Position trading (semaines/mois)", labelEn: "Position trading (weeks/months)", score: 4 },
    ]
  },
  {
    id: 5,
    question: "Quel est votre objectif principal ?",
    questionEn: "What is your main objective?",
    icon: Target,
    options: [
      { label: "Préserver mon capital avec des gains réguliers", labelEn: "Preserve capital with steady gains", score: 1 },
      { label: "Croissance équilibrée avec risque modéré", labelEn: "Balanced growth with moderate risk", score: 2 },
      { label: "Maximiser les gains, même avec plus de risque", labelEn: "Maximize gains, even with more risk", score: 3 },
      { label: "Gains explosifs, je suis prêt à tout perdre", labelEn: "Explosive gains, ready to lose it all", score: 4 },
    ]
  }
];

const PROFILES = {
  prudent: {
    name: "Prudent",
    nameEn: "Prudent",
    description: "Vous privilégiez la sécurité. Levier 10x, TP proche, gains réguliers.",
    descriptionEn: "You prioritize safety. 10x leverage, close TP, steady gains.",
    icon: Shield,
    color: "text-green-400",
    bgColor: "bg-green-500/20",
    leverage: "10x"
  },
  balanced: {
    name: "Équilibré",
    nameEn: "Balanced",
    description: "Vous cherchez un compromis gains/risque. Levier 20x, TP moyen.",
    descriptionEn: "You seek a gains/risk balance. 20x leverage, medium TP.",
    icon: Scale,
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20",
    leverage: "20x"
  },
  dynamic: {
    name: "Dynamique",
    nameEn: "Dynamic",
    description: "Vous visez les gros gains. Levier 30x, TP éloigné, risque élevé.",
    descriptionEn: "You aim for big gains. 30x leverage, far TP, high risk.",
    icon: Flame,
    color: "text-red-400",
    bgColor: "bg-red-500/20",
    leverage: "30x"
  }
};

const Landing = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated, user, updateRiskProfile } = useAuth();
  const navigate = useNavigate();
  
  // Track Record stats
  const [trackRecordStats, setTrackRecordStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [foundersStatus, setFoundersStatus] = useState({ total: 100, taken: 0, remaining: 100, active: true });
  
  // Fetch track record stats
  useEffect(() => {
    const fetchTrackRecord = async () => {
      try {
        const response = await axios.get(`${API}/track-record/public`);
        setTrackRecordStats(response.data);
      } catch (error) {
        console.error('Failed to fetch track record:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchTrackRecord();
  }, []);

  // Fetch founders offer status (live count from backend)
  useEffect(() => {
    axios.get(`${API}/stripe/founders/status`)
      .then((res) => setFoundersStatus(res.data))
      .catch(() => { /* keep defaults */ });
  }, []);
  
  // 3D Logo mouse tracking
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const logoRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!logoRef.current) return;
    const rect = logoRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = (e.clientX - centerX) / (rect.width / 2);
    const y = (e.clientY - centerY) / (rect.height / 2);
    setMousePosition({ x: x * 15, y: y * -15 }); // Max 15 degrees rotation
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };
  
  // Questionnaire state
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showConfirmReevaluate, setShowConfirmReevaluate] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      // Redirect to subscription page
      navigate('/subscribe');
    }
  };

  const handleStartQuestionnaire = () => {
    // If user is authenticated and already has a profile, ask for confirmation
    if (isAuthenticated && user?.risk_profile) {
      setShowConfirmReevaluate(true);
    } else {
      // Start questionnaire directly
      startQuestionnaire();
    }
  };

  const startQuestionnaire = () => {
    setShowConfirmReevaluate(false);
    setShowQuestionnaire(true);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setProfile(null);
  };
  
  const handleAnswer = (score) => {
    const newAnswers = [...answers, score];
    setAnswers(newAnswers);
    
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate profile
      const totalScore = newAnswers.reduce((a, b) => a + b, 0);
      let profileKey;
      if (totalScore <= 8) {
        profileKey = 'prudent';
      } else if (totalScore <= 14) {
        profileKey = 'balanced';
      } else {
        profileKey = 'dynamic';
      }
      setProfile(profileKey);
      setShowResult(true);
      
      // Store profile in localStorage for use after registration
      localStorage.setItem('tradex_risk_profile', profileKey);
      localStorage.setItem('tradex_risk_score', totalScore.toString());
    }
  };
  
  const handleContinueToRegister = () => {
    setShowQuestionnaire(false);
    navigate('/register');
  };

  const handleContinueToLogin = () => {
    setShowQuestionnaire(false);
    navigate('/login');
  };

  const handleSaveProfileForLoggedUser = async () => {
    if (!profile) return;
    
    setSavingProfile(true);
    const totalScore = answers.reduce((a, b) => a + b, 0);
    const success = await updateRiskProfile(profile, totalScore);
    setSavingProfile(false);
    
    if (success) {
      setShowQuestionnaire(false);
      navigate('/analysis');
    }
  };

  const features = [
    {
      icon: Upload,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      icon: Shield,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
      color: 'text-accent',
      bgColor: 'bg-accent/10',
    },
    {
      icon: History,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
      color: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
    },
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Futuristic gray background */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #0d1117 0%, #1a1f2e 25%, #252b3b 50%, #1a1f2e 75%, #0d1117 100%)',
        }}
      />
      {/* Grid overlay for tech effect */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 229, 153, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 229, 153, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
      {/* Background effects */}
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex flex-col justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* LEFT - 3D ANIMATED LOGO */}
            <div 
              className="flex justify-center lg:justify-end perspective-1000 animate-fade-in"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <div 
                ref={logoRef}
                className="relative group cursor-pointer"
                style={{
                  transform: `perspective(1000px) rotateX(${mousePosition.y}deg) rotateY(${mousePosition.x}deg)`,
                  transition: 'transform 0.1s ease-out',
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* Glow effect behind logo */}
                <div 
                  className="absolute inset-0 blur-3xl opacity-50 group-hover:opacity-80 transition-opacity duration-500"
                  style={{
                    background: 'radial-gradient(ellipse at center, rgba(0, 229, 153, 0.4) 0%, transparent 70%)',
                    transform: 'translateZ(-50px) scale(1.5)',
                  }}
                />
                
                {/* Floating animation wrapper */}
                <div className="animate-float">
                  {/* Main logo - 3D futuristic version */}
                  <div className="relative">
                    <img 
                      src="/logo-tradex-3d.jpg" 
                      alt="TRADEX AI" 
                      className="relative w-full max-w-sm lg:max-w-lg h-auto object-contain rounded-xl"
                      style={{ 
                        transform: 'translateZ(30px)',
                        boxShadow: '0 0 60px rgba(0, 229, 153, 0.3), 0 25px 50px rgba(0, 0, 0, 0.5)',
                      }}
                    />
                  </div>
                </div>
                
                {/* Orbiting particles */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/4 left-0 w-2 h-2 bg-primary rounded-full animate-orbit opacity-60" style={{ animationDelay: '0s' }} />
                  <div className="absolute top-0 right-1/4 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-orbit opacity-40" style={{ animationDelay: '-2s' }} />
                  <div className="absolute bottom-1/4 right-0 w-2 h-2 bg-primary rounded-full animate-orbit opacity-50" style={{ animationDelay: '-4s' }} />
                </div>
              </div>
            </div>

            {/* RIGHT - Content */}
            <div className="space-y-6 animate-fade-in text-center lg:text-left">
              {/* Main hook text */}
              <div className="space-y-3">
                <p className="text-xl sm:text-2xl font-semibold text-foreground leading-relaxed">
                  {language === 'fr' 
                    ? "Fini les heures d'hésitation." 
                    : "No more hours of hesitation."}
                </p>
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                  {language === 'fr' 
                    ? "Notre expert IA décrypte vos graphiques et vous propose des setups en temps réel." 
                    : "Our AI expert decrypts your charts and offers you setups in real-time."}
                </p>
                <p className="text-lg sm:text-xl font-medium text-foreground leading-relaxed">
                  {language === 'fr' 
                    ? "Rien de plus simple :" 
                    : "Nothing simpler:"}
                  <span className="text-primary font-bold"> {language === 'fr' ? "Un clic, Un trade." : "One click, One trade."}</span>
                </p>
              </div>

              {/* Free Trial Badge */}
              <div className="flex justify-center lg:justify-start">
                <div className="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-primary/20 to-emerald-500/20 border border-primary/30">
                  <div className="w-10 h-10 rounded-full bg-primary/30 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-primary">
                      {language === 'fr' ? '2 Analyses IA Gratuites' : '2 Free AI Analyses'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {language === 'fr' ? '1 Swing + 1 Intraday à la création de compte' : '1 Swing + 1 Intraday on signup'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 🔥 Offre Fondateurs — CTA principal pour driver l'inscription */}
              {foundersStatus.active && (
                <Link
                  to="/subscribe"
                  className="block group"
                  data-testid="landing-founders-banner"
                >
                  <div className="relative overflow-hidden rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 p-4 hover:border-amber-500/70 transition-all">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Flame className="w-5 h-5 text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-base font-bold text-amber-400 uppercase tracking-wider">
                            {language === 'fr' ? 'Offre Fondateurs' : 'Founders Offer'}
                          </span>
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] font-mono">
                            {foundersStatus.taken}/{foundersStatus.total} {language === 'fr' ? 'prises' : 'taken'}
                          </Badge>
                        </div>
                        <p className="text-sm text-foreground">
                          {language === 'fr'
                            ? <>Les 50 premières places : <strong className="text-amber-300">Expert offert 3 mois</strong>. Les 50 suivantes : <strong className="text-amber-300">Pro offert 3 mois</strong>.</>
                            : <>First 50 spots: <strong className="text-amber-300">Expert free for 3 months</strong>. Next 50: <strong className="text-amber-300">Pro free for 3 months</strong>.</>}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {foundersStatus.remaining > 0
                            ? (language === 'fr'
                                ? <><strong className="text-amber-400">{foundersStatus.remaining} places restantes</strong> · Créez votre compte pour réclamer.</>
                                : <><strong className="text-amber-400">{foundersStatus.remaining} spots left</strong> · Create your account to claim.</>)
                            : (language === 'fr' ? 'Toutes les places ont été prises.' : 'All spots taken.')}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-amber-400 flex-shrink-0 group-hover:translate-x-1 transition-transform mt-1" />
                    </div>
                  </div>
                </Link>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={handleGetStarted}
                  data-testid="get-started-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base font-mono uppercase tracking-wider group"
                >
                  {t('landing.cta')}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                {/* Voir nos abonnements */}
                <Link to="/subscribe" className="w-full sm:w-auto">
                  <Button
                    variant="outline"
                    data-testid="landing-see-plans-btn"
                    className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-6 py-6 text-base group"
                  >
                    <Flame className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Voir nos abonnements' : 'View our plans'}
                  </Button>
                </Link>

                {/* Evaluate Profile Button */}
                <Button
                  onClick={handleStartQuestionnaire}
                  variant="outline"
                  data-testid="evaluate-profile-btn"
                  className="border-white/20 hover:bg-white/5 px-6 py-6 text-base group"
                >
                  <Target className="w-5 h-5 mr-2 text-primary" />
                  {language === 'fr' ? 'Évaluer mon profil' : 'Evaluate my profile'}
                </Button>
              </div>
              
              {/* Profile Questionnaire Explanation */}
              <p className="text-sm text-muted-foreground max-w-lg">
                <span className="text-primary">*</span> {language === 'fr' 
                  ? 'Ce questionnaire permet à notre IA de vous proposer des trades adaptés à votre profil de risque (Prudent, Équilibré ou Dynamique).' 
                  : 'This questionnaire allows our AI to offer you trades adapted to your risk profile (Prudent, Balanced or Dynamic).'}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/10 max-w-sm">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold font-mono text-primary">5+</div>
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Marchés' : 'Markets'}</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold font-mono text-primary">ICT</div>
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Concepts' : 'Concepts'}</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold font-mono text-primary">3</div>
                  <div className="text-sm text-muted-foreground">{language === 'fr' ? 'Profils' : 'Profiles'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Track Record Section — moved right after hero buttons to surface social proof early */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 border-t border-white/5 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-primary/20 text-primary border-primary/30 px-4 py-2 text-sm mb-6">
              <Activity className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Performances vérifiées en temps réel' : 'Real-time verified performance'}
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {language === 'fr' ? 'Notre' : 'Our'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">
                Track Record
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'fr' 
                ? 'Transparence totale. Chaque analyse IA est trackée et mesurée.' 
                : 'Total transparency. Every AI analysis is tracked and measured.'}
            </p>
          </div>

          {loadingStats ? (
            <div className="text-center py-12">
              <Activity className="w-8 h-8 animate-spin text-primary mx-auto" />
            </div>
          ) : trackRecordStats && trackRecordStats.total_analyses > 0 ? (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
                <div className="glass-card p-6 text-center">
                  <div className="text-4xl sm:text-5xl font-bold font-mono text-primary mb-2">
                    {trackRecordStats.total_analyses}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Analyses IA' : 'AI Analyses'}
                  </div>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="text-4xl sm:text-5xl font-bold font-mono text-green-400 mb-2">
                    {(100 - trackRecordStats.loss_rate).toFixed(0)}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'Trades gagnants' : 'Winning trades'}
                  </div>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="text-4xl sm:text-5xl font-bold font-mono text-emerald-400 mb-2">
                    {trackRecordStats.win_rate_tp100.toFixed(0)}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'TP Final atteint' : 'Final TP hit'}
                  </div>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="text-4xl sm:text-5xl font-bold font-mono text-orange-400 mb-2">
                    {trackRecordStats.avg_rr.toFixed(1)}:1
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'R:R Moyen' : 'Average R:R'}
                  </div>
                </div>
              </div>

              {/* Win Rate by Target */}
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <div className="glass-card p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-green-400" />
                    <span className="font-semibold">TP1 (25%)</span>
                  </div>
                  <div className="text-3xl font-bold text-green-400 mb-2">
                    {trackRecordStats.win_rate_tp1.toFixed(0)}%
                  </div>
                  <div className="h-2 bg-green-500/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                      style={{ width: `${trackRecordStats.win_rate_tp1}%` }}
                    />
                  </div>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <span className="font-semibold">TP2 (50%)</span>
                  </div>
                  <div className="text-3xl font-bold text-primary mb-2">
                    {trackRecordStats.win_rate_tp2.toFixed(0)}%
                  </div>
                  <div className="h-2 bg-primary/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-blue-500 rounded-full"
                      style={{ width: `${trackRecordStats.win_rate_tp2}%` }}
                    />
                  </div>
                </div>
                <div className="glass-card p-6 text-center">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Trophy className="w-5 h-5 text-orange-400" />
                    <span className="font-semibold">TP100 (100%)</span>
                  </div>
                  <div className="text-3xl font-bold text-orange-400 mb-2">
                    {trackRecordStats.win_rate_tp100.toFixed(0)}%
                  </div>
                  <div className="h-2 bg-orange-500/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
                      style={{ width: `${trackRecordStats.win_rate_tp100}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Link to="/track-record">
                  <Button variant="outline" className="border-primary/30 hover:bg-primary/10">
                    <Eye className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Voir tous les détails' : 'View all details'}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            /* Testing Phase - No data yet */
            <div className="max-w-2xl mx-auto text-center">
              <div className="glass-card p-8 bg-gradient-to-br from-primary/10 to-emerald-500/10 border-primary/30">
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                  <LineChart className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">
                  {language === 'fr' ? 'Phase de test en cours' : 'Testing phase in progress'}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {language === 'fr' 
                    ? 'Notre IA est actuellement en phase de validation avec des analyses quotidiennes pour garantir des résultats fiables.' 
                    : 'Our AI is currently in validation phase with daily analyses to ensure reliable results.'}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4 mb-6 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    {language === 'fr' ? 'Analyses quotidiennes' : 'Daily analyses'}
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    {language === 'fr' ? 'Multi-marchés' : 'Multi-markets'}
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    {language === 'fr' ? 'Concepts ICT' : 'ICT Concepts'}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' 
                    ? 'Objectif : 150+ trades validés avant lancement officiel' 
                    : 'Goal: 150+ validated trades before official launch'}
                </p>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-8 text-center text-xs text-muted-foreground max-w-3xl mx-auto">
            <p>
              {language === 'fr' 
                ? '⚠️ Les performances passées ne préjugent pas des performances futures. Le trading comporte des risques de perte en capital.' 
                : '⚠️ Past performance does not guarantee future results. Trading involves risk of capital loss.'}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`glass-card p-8 space-y-4 animate-fade-in stagger-${index + 1} card-hover`}
                  data-testid={`feature-${index + 1}`}
                >
                  <div className={`w-14 h-14 rounded-xl ${feature.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-7 h-7 ${feature.color}`} />
                  </div>
                  <h3 className="text-xl font-bold">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trading Rules Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {t('landing.title').includes('IA') ? 'Règles de Trading Essentielles' : 'Essential Trading Rules'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.title').includes('IA') 
                ? 'Notre IA applique ces principes fondamentaux pour chaque analyse'
                : 'Our AI applies these fundamental principles for every analysis'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Risk/Trade', value: '1-2%', desc: t('landing.title').includes('IA') ? 'du capital total' : 'of total capital' },
              { label: 'Min R:R', value: '2:1', desc: t('landing.title').includes('IA') ? 'ratio minimum' : 'minimum ratio' },
              { label: 'Stop Loss', value: '100%', desc: t('landing.title').includes('IA') ? 'toujours utilisé' : 'always used' },
              { label: 'Take Profit', value: 'Partial', desc: t('landing.title').includes('IA') ? 'TP1, TP2, TP100%' : 'TP1, TP2, TP100%' },
            ].map((rule, i) => (
              <div key={i} className="glass-card p-6 text-center">
                <div className="text-3xl font-bold font-mono text-primary mb-2">{rule.value}</div>
                <div className="font-medium mb-1">{rule.label}</div>
                <div className="text-sm text-muted-foreground">{rule.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Risk Profile Questionnaire Modal */}
      <Dialog open={showQuestionnaire} onOpenChange={setShowQuestionnaire}>
        <DialogContent className="max-w-lg w-[95vw] p-0 bg-[#0a0a12] border-white/10 overflow-hidden">
          {!showResult ? (
            // Questions
            <div className="p-6">
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-muted-foreground">
                    Question {currentQuestion + 1} / {QUESTIONS.length}
                  </span>
                  <span className="text-xs text-primary">
                    {Math.round(((currentQuestion + 1) / QUESTIONS.length) * 100)}%
                  </span>
                </div>
                <div className="h-2 bg-secondary/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${((currentQuestion + 1) / QUESTIONS.length) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Question */}
              {(() => {
                const q = QUESTIONS[currentQuestion];
                const Icon = q.icon;
                return (
                  <div className="space-y-6 animate-fade-in">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold leading-tight pt-2">
                        {language === 'fr' ? q.question : q.questionEn}
                      </h3>
                    </div>
                    
                    <div className="space-y-3">
                      {q.options.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleAnswer(option.score)}
                          className="w-full p-4 text-left rounded-xl border border-white/10 bg-secondary/30 hover:bg-primary/20 hover:border-primary/50 transition-all duration-200 group"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium group-hover:text-primary transition-colors">
                              {language === 'fr' ? option.label : option.labelEn}
                            </span>
                            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            // Result
            <div className="p-6 text-center space-y-6 animate-fade-in">
              {(() => {
                const p = PROFILES[profile];
                const Icon = p.icon;
                return (
                  <>
                    <div className="flex justify-center">
                      <div className={`p-6 rounded-full ${p.bgColor}`}>
                        <Icon className={`w-16 h-16 ${p.color}`} />
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">
                        {language === 'fr' ? 'Votre profil de risque' : 'Your risk profile'}
                      </div>
                      <h2 className={`text-3xl font-bold ${p.color}`}>
                        {language === 'fr' ? p.name : p.nameEn}
                      </h2>
                    </div>
                    
                    <p className="text-muted-foreground">
                      {language === 'fr' ? p.description : p.descriptionEn}
                    </p>
                    
                    <div className="flex items-center justify-center gap-6 py-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold font-mono">{p.leverage}</div>
                        <div className="text-xs text-muted-foreground">Levier</div>
                      </div>
                      <div className="w-px h-10 bg-white/10" />
                      <div className="text-center">
                        <div className="text-2xl font-bold font-mono text-primary">
                          {answers.reduce((a, b) => a + b, 0)}/20
                        </div>
                        <div className="text-xs text-muted-foreground">Score</div>
                      </div>
                    </div>
                    
                    {/* Different buttons based on auth status */}
                    {isAuthenticated ? (
                      // User is logged in - save profile and go to analysis
                      <Button
                        onClick={handleSaveProfileForLoggedUser}
                        disabled={savingProfile}
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-base font-mono uppercase tracking-wider group"
                      >
                        {savingProfile ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            {language === 'fr' ? 'Enregistrement...' : 'Saving...'}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5 mr-2" />
                            {language === 'fr' ? 'Enregistrer et analyser' : 'Save and analyze'}
                            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                          </>
                        )}
                      </Button>
                    ) : (
                      // User not logged in - show login/register options
                      <div className="space-y-3">
                        <Button
                          onClick={handleContinueToRegister}
                          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-base font-mono uppercase tracking-wider group"
                        >
                          <CheckCircle className="w-5 h-5 mr-2" />
                          {language === 'fr' ? 'Créer mon compte' : 'Create my account'}
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                        <Button
                          onClick={handleContinueToLogin}
                          variant="outline"
                          className="w-full py-4 border-white/20"
                        >
                          {language === 'fr' ? "J'ai déjà un compte" : 'I already have an account'}
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for re-evaluation */}
      <Dialog open={showConfirmReevaluate} onOpenChange={setShowConfirmReevaluate}>
        <DialogContent className="max-w-md bg-[#0a0a12] border-white/10">
          <div className="text-center space-y-6 py-4">
            {user?.risk_profile && PROFILES[user.risk_profile] && (
              <>
                <div className="flex justify-center">
                  <div className={`p-4 rounded-full ${PROFILES[user.risk_profile].bgColor}`}>
                    {(() => {
                      const Icon = PROFILES[user.risk_profile].icon;
                      return <Icon className={`w-10 h-10 ${PROFILES[user.risk_profile].color}`} />;
                    })()}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2">
                    {language === 'fr' ? 'Vous avez déjà un profil' : 'You already have a profile'}
                  </h3>
                  <p className="text-muted-foreground">
                    {language === 'fr' ? 'Votre profil actuel :' : 'Your current profile:'}{' '}
                    <span className={`font-bold ${PROFILES[user.risk_profile].color}`}>
                      {language === 'fr' ? PROFILES[user.risk_profile].name : PROFILES[user.risk_profile].nameEn}
                    </span>
                    {' '}({PROFILES[user.risk_profile].leverage})
                  </p>
                </div>

                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Voulez-vous refaire le questionnaire pour réévaluer votre profil de risque ?' 
                    : 'Do you want to redo the questionnaire to re-evaluate your risk profile?'}
                </p>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmReevaluate(false)}
                    className="flex-1 border-white/20"
                  >
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button
                    onClick={startQuestionnaire}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {language === 'fr' ? 'Réévaluer' : 'Re-evaluate'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
